import { useEffect, useState } from 'react';
import { teamsSchema, type AppSettings } from '@ace-tracker/shared';
import { Button } from '../Button';
import { Card, IconButton, SaveBar, TextInput } from './formParts';
import { useSaveSettings } from './useSaveSettings';

export function TeamsSettings({ settings }: { settings: AppSettings }) {
  const [teams, setTeams] = useState(() => settings.teams);
  const [name, setName] = useState('');
  const [validationError, setValidationError] = useState<string>();
  const { save, saving, error, savedAt, reset } = useSaveSettings('teams');

  const baselineKey = JSON.stringify(settings.teams);
  useEffect(() => setTeams(settings.teams), [baselineKey]);

  const dirty = JSON.stringify(teams) !== JSON.stringify(settings.teams);

  function addTeam() {
    const candidate = name.trim();
    const result = teamsSchema.safeParse([...teams, candidate]);
    if (!result.success) {
      setValidationError(result.error.issues[0].message);
      return;
    }
    setTeams(result.data.sort((a, b) => a.localeCompare(b)));
    setName('');
    setValidationError(undefined);
  }

  return (
    <div className="space-y-5">
      <Card title="Developer teams" description="Create and save teams here before assigning developers on the Developers tab.">
        <label htmlFor="team-add" className="block text-sm font-medium text-text-default">Team name</label>
        <div className="mt-1.5 flex flex-wrap items-start gap-3">
          <div className="min-w-[14rem] flex-1 sm:max-w-sm">
            <TextInput
              id="team-add"
              maxLength={100}
              value={name}
              invalid={Boolean(validationError)}
              aria-describedby={validationError ? 'team-add-error' : undefined}
              onChange={(event) => {
                setName(event.target.value);
                setValidationError(undefined);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTeam();
                }
              }}
            />
            {validationError && <p id="team-add-error" className="mt-1 text-xs text-error">{validationError}</p>}
          </div>
          <Button type="button" variant="secondary" onClick={addTeam} disabled={!name.trim()}>
            <i aria-hidden="true" className="fa-solid fa-plus" />
            Add team
          </Button>
        </div>
        <ul className="mt-5 divide-y divide-border border-t border-border">
          {teams.map((team) => {
            const memberCount = settings.developers.filter((developer) => developer.team?.toLowerCase() === team.toLowerCase()).length;
            return (
              <li key={team} className="flex min-h-[52px] items-center justify-between gap-3 py-1 text-sm">
                <span className="min-w-0 break-words text-text-default">{team}</span>
                <span className="ml-auto shrink-0 text-xs text-text-muted">{memberCount} {memberCount === 1 ? 'developer' : 'developers'}</span>
                {memberCount > 0 ? (
                  <span className="w-11 shrink-0 text-center text-xs text-text-muted" title="Reassign developers before removing this team">In use</span>
                ) : (
                  <IconButton icon="fa-solid fa-trash" label={`Remove ${team}`} onClick={() => setTeams((names) => names.filter((item) => item !== team))} />
                )}
              </li>
            );
          })}
          {teams.length === 0 && <li className="py-4 text-sm text-text-muted">No teams yet.</li>}
        </ul>
      </Card>
      <SaveBar
        dirty={dirty}
        saving={saving}
        error={error}
        savedAt={savedAt}
        audit={settings.audit.teams}
        onSave={() => save(teams)}
        onDiscard={() => {
          setTeams(settings.teams);
          setName('');
          setValidationError(undefined);
          reset();
        }}
      />
    </div>
  );
}